import { ChangeEvent, ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import { useTimeout } from 'webrix/hooks';
import OtpInput from '../shared/OtpInput';
import { useMutation } from 'react-query';
import { sendOtp, verifyOtp } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { useGetPhoneVerificationStatus } from 'src/hooks/user.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { get } from 'lodash';
import { isPhoneOrEmail } from 'shared-utils';
import { sendTrackingEvent } from 'src/utils/gtm';
import { isRecruiterSide } from 'src/config/env.config';
import Container from '../shared/Container';
import AttendanceConfirmed from './AttendanceConfirmed';
import { OfflineDocumentsTypes } from 'types';

interface Props {
	driveUserPhone: string;
	btnText?: string;
	changeOtpMethod?: boolean;
	setChangeOtpMethod?: (phoneOrEmail: boolean) => void;
	setDriveUserPhone?: (driveUserPhone: string) => void;
	setOtpSubmitted?: (otpSubmitted: boolean) => void;
	setIsOpenOtpDrawer?: (isOpenOtpDrawer: boolean) => void;
	setSelectedDrive?: (drive: { label: string; value: string }) => void;
	selectedDrive?: { label: string; value: string };
	formRegisteredDriveData?: any;
	documents: OfflineDocumentsTypes[];
	selectedLocation?: { label: string; value: string } | null;
}

const DrivePhoneVerification = ({
	driveUserPhone,
	btnText,
	setChangeOtpMethod,
	setDriveUserPhone,
	setOtpSubmitted,
	setIsOpenOtpDrawer,
	setSelectedDrive,
	selectedDrive,
	formRegisteredDriveData,
	documents,
	selectedLocation,
}: Props) => {
	const location = useLocation();
	const { selectedDrive: selectedDriveGlobal } = useUserContext();

	const navigate = useNavigate();
	const toast = useToast();
	const { search } = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otp, setOtp] = useState(['', '', '', '', '', '']);
	const [time, setTime] = useState(30);
	const [isResendEnabled, setIsResendEnabled] = useState(false);
	const [isMobileScreen, setIsMobileScreen] = useState(false);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const [isVerified, setIsVerified] = useState(false);
	const { start, stop } = useTimeout(() => {
		if (time > 0) {
			setTime(time - 1);
		}
	}, 1000);

	useEffect(() => {
		if (time <= 0) {
			setIsResendEnabled(true);
			stop();
		}
	}, [stop, time]);

	useEffect(() => {
		start();
	}, [start]);

	const phoneVerificationStatus = useGetPhoneVerificationStatus(driveUserPhone);

	const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
		const newOtp = [...otp];
		newOtp[index] = event.target.value;
		setOtp(newOtp);
		if (event.target.value && index < otp.length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Backspace' && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasteData = event.clipboardData.getData('text/plain').slice(0, 6);
		const newOtp = [...otp];
		pasteData?.split('').forEach((digit, index) => {
			newOtp[index] = digit;
		});
		setOtp(newOtp);
	};

	const verifyPhoneFn = useMutation({
		mutationKey: 'verifyOtp',
		mutationFn: verifyOtp,
		onSuccess() {
			const type = isPhoneOrEmail(driveUserPhone);
			toast({
				type: 'success',
				title: `${type} verified successfully.`,
			});
			setIsLoading(false);
			phoneVerificationStatus.refetch();
			setIsVerified(true);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const generateVerificationCodeFn = useMutation({
		mutationKey: 'sendOtp',
		mutationFn: sendOtp,
		onSuccess() {
			toast({
				type: 'success',
				title: 'An OTP has sent to your registered email.',
			});
			setIsResendEnabled(false);
			setIsLoading(false);
			phoneVerificationStatus.refetch();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		sendTrackingEvent({
			event: 'verify_code',
			eventLabel: location.pathname,
		});
		e.preventDefault();
		setIsLoading(true);
		const payload = {
			emailOrPhone: driveUserPhone.trim().toLowerCase(),
			otp: otp.join(''),
			otpSessionId: phoneVerificationStatus.data?.verification?.otpSessionId || '',
			driveId: selectedDrive?.value == undefined ? formRegisteredDriveData?.driveId : selectedDrive?.value,
			driveLabel: selectedLocation?.label || undefined, // Pass the selected drive label
			offlineDriveRegistration: formRegisteredDriveData,
			offlineDriveDocuments: documents,
		};
		console.log('payload:', payload);
		verifyPhoneFn.mutate(payload);
	};

	useEffect(() => {
		setIsMobileScreen(window.innerWidth <= 960);
	}, []);

	if (isVerified) {
		return <AttendanceConfirmed driveID={selectedDrive?.value || ''} DriveIdGlobal={selectedDriveGlobal?.value} />;
	}

	return (
		<Container>
			<div className="max-w-[560px] px-4 md:px-0 mx-auto rounded-[8px] justify-center">
				<div className="items-center text-center content-center max-w-[492px] mx-auto pt-5 md:pt-[36px]">
					<h6 className="ffont-inter text-[20px] font-[700] leading-[24px] text-[#1C1C28]">
						Digital attendance for {selectedDrive?.label}
					</h6>
				</div>
				<div className="flex flex-col items-center gap-[16px] justify-center max-w-[358px] mx-auto pt-[32px]">
					<form className={'flex flex-col gap-4 shrink-0'} onSubmit={handleSubmit}>
						<div className="dark:text-white text-[#28293D] md:w-[358px]">
							<div className="mt-4">
								<span className="email_verification_para_text font-inter">{`Check the code sent `}</span>
								<span className="dark:!text-[#CDF] text-[#28293D] email_verification_para_text font-inter">
									{driveUserPhone?.trim() || ''}{' '}
								</span>
								<span className="email_verification_para_text font-inter">{`and enter it below.`}</span>
								<span
									className="cursor-pointer ml-2 dark:text-[#6698FA] text-[#7777E7] text-sm underline"
									onClick={() => {
										if (isRecruiterSide) {
											sendTrackingEvent({
												event: 'recr_change_email',
												eventLabel: location.pathname,
											});
										} else {
											sendTrackingEvent({
												event: 'change_mobile',
												eventLabel: location.pathname,
											});
										}
										setChangeOtpMethod && setChangeOtpMethod(true);
										setDriveUserPhone && setDriveUserPhone('');
										setOtpSubmitted && setOtpSubmitted(false);
										setIsOpenOtpDrawer && setIsOpenOtpDrawer(false);
									}}
								>
									{!isRecruiterSide ? 'Change phone' : 'Change phone'}
								</span>
							</div>
						</div>
						<div className="md:w-[358px]">
							<OtpInput
								isMobileScreen={isMobileScreen}
								handlePaste={handlePaste}
								handleKeyDown={handleKeyDown}
								handleChange={handleChange}
								otp={otp}
								inputRefs={inputRefs}
							/>

							<div className="mt-[4px]">
								{time > 0 && !isResendEnabled ? (
									<div className="cursor-not-allowed">
										<span className="text-sm text-[#3F83F8]">Resend code in </span>
										<span className="dark:text-[#C7C9D9] text-[#555770]">{time} sec</span>
									</div>
								) : (
									<span
										className="cursor-pointer text-sm text-[#3F83F8]"
										onClick={() => {
											sendTrackingEvent({
												event: 'resend_code',
												eventLabel: location.pathname,
											});
											if (isResendEnabled) {
												setOtp(['', '', '', '', '', '']);
												generateVerificationCodeFn.mutate({ emailOrPhone: driveUserPhone });
												setTime(30);
												start();
											}
										}}
									>
										Resend Code
									</span>
								)}
							</div>
						</div>

						<div className="max-w-[332px] pb-14 md:mt-4">
							<Button
								color="purplePlaced"
								disabled={isLoading || otp.join('').length < 6}
								type="submit"
								className={' w-[100%] bg-[#7777E7]'}
							>
								{isLoading ? <Spinner /> : btnText || 'Verify'}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</Container>
	);
};

export default DrivePhoneVerification;
